/**
 * Shared classification-confidence band thresholds and colours.
 *
 * One source of truth for the green / amber / red banding used across the
 * confidence visualisations (the Document classification table, the category
 * view's Pages table and its page-sequence strip), so a given score always
 * renders the same band everywhere.
 */
export const CONFIDENCE_BANDS = {
  /** score >= this renders green ("confident") */
  high: 0.85,
  /** score >= this (and below `high`) renders amber ("uncertain") */
  medium: 0.7,
} as const;

/**
 * Margin from the top-1 score below which a runner-up counts as a real
 * contender. Scale-invariant (a gap, not an absolute score), so it survives
 * embedding-space changes. Derived from the 39k-page labeled dev corpus
 * (DR-2073): pages whose top-1 is wrong have margins mostly below ~0.054
 * (p75), pages whose top-1 is right mostly above ~0.062 (p25).
 */
export const CONTESTED_MARGIN = 0.05;

/** Tailwind text classes for a confidence score's band. */
export function confidenceBandText(score: number): string {
  if (score >= CONFIDENCE_BANDS.high) {
    return 'text-green-500 dark:text-green-400';
  }
  if (score >= CONFIDENCE_BANDS.medium) {
    return 'text-amber-500 dark:text-amber-400';
  }
  return 'text-red-500 dark:text-red-400';
}

/** Tailwind background classes for a confidence score's band (e.g. strip cells). */
export function confidenceBandBg(score: number): string {
  if (score >= CONFIDENCE_BANDS.high) {
    return 'bg-green-500 dark:bg-green-400';
  }
  if (score >= CONFIDENCE_BANDS.medium) {
    return 'bg-amber-500 dark:bg-amber-400';
  }
  return 'bg-red-500 dark:bg-red-400';
}

/** Pale chip background classes for a confidence score's band (badge chips). */
export function confidenceBandChipBg(score: number): string {
  if (score >= CONFIDENCE_BANDS.high) {
    return 'bg-green-100 dark:bg-green-700';
  }
  if (score >= CONFIDENCE_BANDS.medium) {
    return 'bg-amber-100 dark:bg-yellow-600';
  }
  return 'bg-red-100 dark:bg-red-700';
}

/**
 * Text classes for a runner-up row in a top-5 classification list, colored by
 * its margin from the top-1 score rather than its absolute score: a close
 * runner-up ("contested") draws attention, a safely-beaten one stays muted.
 */
export function runnerUpMarginText(margin: number): string {
  return margin < CONTESTED_MARGIN ? 'text-amber-500 dark:text-amber-400' : 'text-gray-400 dark:text-zinc-500';
}
